import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  MapPin,
  Map,
  Calendar,
  Package,
  CreditCard,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  XCircle,
  Clock,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../store/slices/authSlice";
import styles from "./OrderDetails.module.css";

export default function OrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const STATUS_MAP = {
    pending_supplier: {
      label: t("orderDetails.status.pending"),
      color: "#f97316",
      bg: "rgba(249,115,22,0.1)",
      icon: Clock,
    },
    accepted: {
      label: t("orderDetails.status.accepted"),
      color: "#0c6839",
      bg: "rgba(12,104,57,0.1)",
      icon: CheckCircle,
    },
    rejected: {
      label: t("orderDetails.status.rejected"),
      color: "#ef4444",
      bg: "rgba(239,68,68,0.1)",
      icon: XCircle,
    },
  };

  const INVOICE_STATUS_MAP = {
    awaiting_payment: {
      label: t("orderDetails.invoiceStatus.awaiting"),
      color: "#f97316",
      bg: "rgba(249,115,22,0.12)",
    },
    paid: {
      label: t("orderDetails.invoiceStatus.paid"),
      color: "#0c6839",
      bg: "rgba(12,104,57,0.12)",
    },
  };

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      setLoading(true);
      try {
        const statuses = ["pending_supplier", "accepted", "rejected"];
        let found = null;
        for (const status of statuses) {
          const res = await api.get("/supplier-materials/purchase-orders", {
            params: { status },
          });
          if (res.data.code === 200) {
            const match = (res.data.data || []).find(
              (o) => String(o.id) === String(id),
            );
            if (match) {
              found = match;
              break;
            }
          }
        }
        if (found) setOrder(found);
        else setError(t("orderDetails.errorNotFound"));
      } catch (err) {
        setError(t("orderDetails.errorFailed"));
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id, t]);

  const handlePayNow = async () => {
    if (!order?.invoice?.id) return;
    setPayLoading(true);
    try {
      const res = await api.post(
        `/supplier-materials/purchase-orders/${order.id}/fawaterak/init`,
      );
      if (res.data.code === 200) {
        const url =
          res.data.data?.checkout_url ||
          res.data.data?.payment_data?.redirectTo;
        if (url) window.open(url, "_blank");
      }
    } catch (err) {
      console.error("Payment init error:", err);
    } finally {
      setPayLoading(false);
    }
  };

  const formatPrice = (val, currencyKey = "EGP") => {
    if (val === null || val === undefined || val === "") return "—";
    const formatted = parseFloat(val).toLocaleString(
      isRtl ? "ar-EG" : "en-EG",
      {
        minimumFractionDigits: 2,
      },
    );
    return `${formatted} ${t(`currency.${currencyKey}`)}`;
  };

  if (loading) {
    return (
      <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.pageHeader}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
          </button>
          <h1 className={styles.pageTitle}>{t("orderDetails.title")}</h1>
        </div>
        <div className={styles.skeletonWrap}>
          {[...Array(4)].map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      </div>
    );
  }

  const status = STATUS_MAP[order.status] || STATUS_MAP.pending_supplier;
  const StatusIcon = status.icon;
  const invoiceStatus = order.invoice
    ? INVOICE_STATUS_MAP[order.invoice.status] ||
      INVOICE_STATUS_MAP.awaiting_payment
    : null;

  const hasQuote = !!order.supplier_quote;
  const hasInvoice = !!order.invoice;
  const canPay =
    hasInvoice &&
    order.invoice.status === "awaiting_payment" &&
    !order.is_paid &&
    order.status === "accepted";

  const canChat = hasInvoice;

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.pageHeader}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          {isRtl ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
        </button>
        <h1 className={styles.pageTitle}>{t("orderDetails.title")}</h1>
      </div>

      <div className={styles.content}>
        <div className={styles.card}>
          <div className={styles.orderTop}>
            <div className={styles.orderIconWrap}>
              <Package size={22} color="#0c6839" />
            </div>
            <div className={styles.orderMeta}>
              <p className={styles.orderNoLabel}>
                {t("orderDetails.orderNoLabel")}:
              </p>
              <p className={styles.orderNo}>
                {order.invoice?.invoice_number || `PO-${order.id}`}
              </p>
            </div>
            <span
              className={styles.statusBadge}
              style={{
                color: invoiceStatus ? invoiceStatus.color : status.color,
                background: invoiceStatus ? invoiceStatus.bg : status.bg,
              }}
            >
              {!invoiceStatus && <StatusIcon size={12} />}
              {invoiceStatus ? invoiceStatus.label : status.label}
            </span>
          </div>

          <div className={styles.divider} />

          <div className={styles.infoRows}>
            {[
              {
                icon: Phone,
                label: t("orderDetails.recipientPhone"),
                value: order.recipient_phone,
              },
              {
                icon: MapPin,
                label: t("orderDetails.location"),
                value: [
                  order.location?.city?.name,
                  order.location?.governorate?.name,
                ]
                  .filter(Boolean)
                  .join(" - "),
              },
              {
                icon: Map,
                label: t("orderDetails.detailedArea"),
                value: order.distinguishing_mark,
              },
              {
                icon: Calendar,
                label: t("orderDetails.orderDate"),
                value: order.created_at?.split("T")[0],
              },
            ].map((row, idx) => (
              <div key={idx} className={styles.infoRow}>
                <span className={styles.infoIcon}>
                  <row.icon size={15} color="#9ca3af" />
                </span>
                <span className={styles.infoLabel}>{row.label}</span>
                <span className={styles.infoValue}>{row.value || "—"}</span>
              </div>
            ))}
          </div>
        </div>

        <p className={styles.sectionTitle}>{t("orderDetails.items")}</p>

        <div className={styles.itemCard}>
          <div className={styles.itemImageWrap}>
            {order.supplier_material?.image ? (
              <img
                src={order.supplier_material.image}
                alt=""
                className={styles.itemImage}
              />
            ) : (
              <div className={styles.itemImagePlaceholder}>
                <Package size={24} color="#0c6839" />
              </div>
            )}
          </div>
          <div className={styles.itemInfo}>
            <p className={styles.itemName}>{order.supplier_material?.name}</p>
            <p className={styles.itemSupplier}>
              {order.supplier_material?.supplier?.name}
            </p>
            <div className={styles.itemBottom}>
              <span className={styles.qtyBadge}>
                {t("orderDetails.quantity")}: {order.quantity}
              </span>
              <div className={styles.priceGroup}>
                <span className={styles.priceNew}>
                  {formatPrice(
                    hasQuote
                      ? order.supplier_quote.unit_price
                      : order.listed_unit_price,
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.detailsCard}>
          <div className={styles.detailsHeader}>
            <CreditCard size={18} color="#0c6839" />
            <span>{t("orderDetails.summaryTitle")}</span>
          </div>
          <SummaryRow
            label={t("orderDetails.subtotal")}
            value={formatPrice(
              hasQuote
                ? order.supplier_quote.line_subtotal
                : order.estimated_subtotal,
            )}
          />
          {hasQuote && order.supplier_quote.discount_percent && (
            <SummaryRow
              label={`${t("orderDetails.discount")} (${order.supplier_quote.discount_percent}%)`}
              value={`- ${formatPrice(order.supplier_quote.discount_total || order.supplier_quote.discount_amount)}`}
              isDiscount
            />
          )}
          <div className={styles.divider} />
          <div className={`${styles.detailRow} ${styles.totalRow}`}>
            <span className={styles.totalLabel}>{t("orderDetails.total")}</span>
            <span className={styles.totalValue}>
              {formatPrice(
                hasInvoice
                  ? order.invoice.grand_total
                  : hasQuote
                    ? order.supplier_quote.grand_total
                    : order.estimated_subtotal,
                hasInvoice ? order.invoice.currency : "EGP",
              )}
            </span>
          </div>
        </div>
      </div>

      {(canPay || canChat) && (
        <div className={styles.ctaBar}>
          {canChat && (
            <button
              className={styles.chatBtn}
              onClick={() => navigate(`/orders/${order.id}/chat`)}
              aria-label={t(
                "orderDetails.chatWithSupplier",
                "Chat with supplier",
              )}
            >
              <MessageCircle size={22} color="#0c6839" />
            </button>
          )}
          {canPay && (
            <button
              className={styles.payBtn}
              onClick={handlePayNow}
              disabled={payLoading}
            >
              {payLoading
                ? t("orderDetails.processing")
                : t("orderDetails.payNow")}
              {!payLoading &&
                (isRtl ? (
                  <ChevronLeft size={18} />
                ) : (
                  <ChevronRight size={18} />
                ))}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, isDiscount }) {
  return (
    <div className={styles.detailRow}>
      <span className={styles.detailLabel}>{label}</span>
      <span
        className={styles.detailValue}
        style={isDiscount ? { color: "#0c6839" } : {}}
      >
        {value}
      </span>
    </div>
  );
}
